import { useCallback } from 'react';
import { Outlet, useMatch, useNavigate } from 'react-router-dom';

import { Unarray } from '@pukka/common/types';
import { dateUtils, numberUtils } from '@pukka/common/utils';
import { TableV2 } from '@pukka/ui-components';
import { ColumnDef, Row, TableOptions, getExpandedRowModel, getGroupedRowModel } from '@tanstack/react-table';
import { useSnapshot } from 'valtio';

import { SummaryHeader } from './SummaryHeader';
import { CancelledTransactionState, ITransactionCancelledState, TransactionState, useCancelledTransactionData } from './Transaction.state';

type IData = Unarray<ITransactionCancelledState['data']>;

const columns: ColumnDef<IData>[] = [
  {
    header: 'Outlet',
    accessorKey: 'outletName',
    cell(props) {
      return props.row.getIsGrouped() ? props.row.groupingValue : props.getValue();
    },
  },
  {
    header: 'Cancelled Time',
    accessorFn(row) {
      return dateUtils.formatDate(row.date, 'HH:mm');
    },
  },
  {
    header: 'Cancelled By',
    accessorKey: 'by',
  },
  {
    header: 'Cancel Reason',
    accessorKey: 'reason',
  },
  {
    accessorKey: 'group',
  },
  {
    header: 'Total Price',
    accessorKey: 'total',
    meta: {
      className: 'text-right',
    },
    cell(props) {
      return numberUtils.formatCurrency(props.getValue() as number, 'Rp.');
    },
  },
];

const options: Partial<TableOptions<IData>> = {
  getExpandedRowModel: getExpandedRowModel(),
  getGroupedRowModel: getGroupedRowModel(),
  groupedColumnMode: 'remove',
  initialState: {
    grouping: ['group'],
    expanded: true,
  },
  defaultColumn: {
    size: 50,
  },
};

function CancelledTransactionPagelet() {
  const match = useMatch('v2/reports/transactions/:tabId/:id?/:token?');
  const id = match?.params.id as string | undefined;
  const token = match?.params.token as string | undefined;
  const { cancelled } = useSnapshot(TransactionState);
  const { data, error, status } = useSnapshot(CancelledTransactionState);
  const navigate = useNavigate();

  const onRowClick = useCallback(
    (row: IData) => {
      navigate(`/v2/reports/transactions/cancelled/${row.outletId}/${row.token}`, {
        unstable_viewTransition: true,
        preventScrollReset: true,
      });
    },
    [navigate],
  );

  const isRowHighlighted = useCallback(
    (row: Row<IData>) => {
      return row.original.outletId === Number(id) && row.original.token === token;
    },
    [id, token],
  );

  const _status = useCancelledTransactionData();

  return (
    <>
      <SummaryHeader loading={_status === 'loading'} className="mb-4">
        <SummaryHeader.Item title="TRANSACTIONS" sum={cancelled?.count ?? 0} />
        <SummaryHeader.Item title="TOTAL CANCELLED" sum={cancelled?.total ?? 0} inCurrencyFormat />
      </SummaryHeader>
      <div className="flex flex-row items-start">
        <div className="flex-1 view-transition-[tx-table]">
          <TableV2
            id="cancelled-transactions"
            columns={columns}
            data={data as IData[]}
            bordered
            rounded
            options={options}
            className="table-fixed"
          >
            <TableV2.Header />
            {status === 'success' || data.length > 0 ? <TableV2.Body isRowHighlighted={isRowHighlighted} onRowClick={onRowClick} /> : null}
            {status === 'loading' ? <TableV2.Loading /> : null}
            {status === 'error' ? <TableV2.Error>{error}</TableV2.Error> : null}
          </TableV2>
        </div>
        <Outlet />
      </div>
    </>
  );
}

export { CancelledTransactionPagelet };
